import useSWR from "swr";
import { useMemo } from "react";

import { IUserItem, IUserSettings, updatePassword } from "src/types/user";
import axios, { fetcher, endpoints } from "src/utils/axios";
// ----------------------------------------------------------------------

export function useGetUserProfile() {
  const URL = endpoints.auth.profile;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      user: (data as IUserItem) || {},
      userLoading: isLoading,
      userError: error,
      userValidating: isValidating,
      userEmpty: !isLoading && !data?.length,
    }),
    [data?.user, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateUserPassword(data: updatePassword) {
  return await axios.put(endpoints.auth.password, data);
}

export async function updateUserSettings(data: IUserSettings) {
  return await axios.put(endpoints.auth.settings, data);
}

const SAMPLE_USER_SETTINGS_PARTIAL: IUserSettings = {
  notificationLanguage: "en",
  activitiesDefaultTab: "occasional_group_trainings",

  globalNotifications: {
    system_critical: {
      inApp: true,
      email: true,
      push: false,
      sms: false,
    },
    system_regular: {
      inApp: true,
      email: true,
      push: false,
      sms: true,
    },
    system_marketing: {
      inApp: true,
      email: false,
      push: false,
      sms: false,
    },
  },

  providerNotifications: {
    48: {
      inApp: true,
      email: true, // opted in
      push: false,
      sms: true,
    },
  },
};

export function useGetUserSettings() {
  const URL = endpoints.auth.settings;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      userSettings: (data as IUserSettings) || {},

      userSettingsLoading: isLoading,
      userSettingsError: error,
      userSettingsValidating: isValidating,
      userSettingsEmpty: !isLoading && !data?.length,
    }),
    [data?.userSettings, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUserTransactions() {
  const URL = endpoints.user.transactions;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUserTransactionTypes() {
  const URL = endpoints.user.transactions + "/types";
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      transactiontypes: data || [],
      transactiontypesLoading: isLoading,
      transactiontypesError: error,
      transactiontypesValidating: isValidating,
      transactiontypesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUserPasses() {
  const URL = endpoints.user.passes;
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true, // Fetches data again when the window regains focus
      refreshInterval: 0, // Disable periodic polling
    }
  );
  const memoizedValue = useMemo(
    () => ({
      passes: data || [],
      passesLoading: isLoading,
      passesError: error,
      passesValidating: isValidating,
      passesEmpty: !isLoading && !data?.length,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetUserTickets() {
  const URL = endpoints.user.tickets;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      tickets: data || [],
      ticketsLoading: isLoading,
      ticketsError: error,
      ticketsValidating: isValidating,
      ticketsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetAdminUsers(providerId: number) {
  const URL = endpoints.user.admin_users + "/" + providerId;
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true, // Fetches data again when the window regains focus
      refreshInterval: 0, // Disable periodic polling
    }
  );
  const memoizedValue = useMemo(
    () => ({
      adminusers: data || [],
      adminusersLoading: isLoading,
      adminusersError: error,
      adminusersValidating: isValidating,
      adminusersEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetPlayerNotifications() {
  const URL = endpoints.user.notifications;
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true, // Fetches data again when the window regains focus
      refreshInterval: 0, // Disable periodic polling
    }
  );
  const memoizedValue = useMemo(
    () => ({
      notifications: data || [],
      notificationsLoading: isLoading,
      notificationsError: error,
      notificationsValidating: isValidating,
      notificationsEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function playerNotificationsMarkAllRead() {
  return await axios.post(endpoints.user.notifications_mark_all_read);
}

export async function playerNotificationsMarkOneRead(notification_id: any) {
  return await axios.post(
    endpoints.user.notifications_mark_one_read,
    notification_id
  );
}

export async function createAdminUser(data: any, provider_id: number) {
  return await axios.post(endpoints.user.admin_users + "/" + provider_id, data);
}

export async function deleteAdminUser(
  provider_id: number,
  user_role_id: number
) {
  return await axios.delete(
    endpoints.user.admin_users + "/" + provider_id + "/" + user_role_id
  );
}

export function useGetUserCustomFields(userid: string, provider_id: number) {
  const URL =
    endpoints.user.customfields.list + "/" + userid + "/" + provider_id;
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true, // Fetches data again when the window regains focus
      refreshInterval: 0, // Disable periodic polling
    }
  );
  const memoizedValue = useMemo(
    () => ({
      userCustomFields: (data as any) || {},
      userCustomFieldsLoading: isLoading,
      userCustomFieldsError: error,
      userCustomFieldsValidating: isValidating,
      userCustomFieldsEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data?.userCustomFields, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function updateCustomField(field_id: string, data: any) {
  return await axios.put(
    endpoints.user.customfields.root + "/" + field_id,
    data
  );
}

export async function deleteUserCustomField(itemId: number) {
  return await axios.delete(endpoints.user.customfields.root + "/" + itemId);
}

export async function setUserCustomField(
  user_id: string,
  field_id: string,
  data: any
) {
  return await axios.post(
    endpoints.user.customfields.list + "/" + user_id + "/field/" + field_id,
    data
  );
}

export async function deleteUserProfile() {
  return await axios.post(endpoints.auth.deleteprofile);
}

export type IMyTeamItem = {
  id: number;
  name: string;
  sport: string;
};

export async function fetchMyTeamsBySport(
  providerId: number,
  sport: string
): Promise<IMyTeamItem[]> {
  const normalizedSport = (sport || "").trim().toLowerCase();

  const res = await axios.get(
    endpoints.user.team.myTeamsByProviderSport(providerId, normalizedSport),
    {
      params: { sport },
    }
  );
  return res.data;
}

export function useGetMyTeams() {
  const URL = endpoints.user.team.myTeams;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 0,
    }
  );

  const memoizedValue = useMemo(
    () => ({
      myTeams: (data as IMyTeamItem[]) || [],
      myTeamsLoading: isLoading,
      myTeamsError: error,
      myTeamsValidating: isValidating,
      myTeamsEmpty: !isLoading && !(data as IMyTeamItem[])?.length,
      myTeamsMutate: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function createMyTeam(data: any, provider_id: number) {
  return await axios.post(endpoints.user.team.createMyTeam(provider_id), data);
}

export async function fetchTeamsByCategory(
  providerId: number,
  sport: string
): Promise<IMyTeamItem[]> {
  const normalizedSport = (sport || "").trim().toLowerCase();

  const res = await axios.get(
    endpoints.user.team.myTeamsByProviderSport(providerId, normalizedSport),
    {
      params: { sport },
    }
  );
  return res.data;
}
